<template>
  <div class="create-lesson">
    <div class="lesson-card">
      <div class="card-header">
        <div class="header-icon">
          <CreateIcon width="48" height="48" />
        </div>
        <h1 class="card-title">Create Tech-Infused Lessons</h1>
        <p class="card-subtitle">
          Create a unique lesson integrated with free technology tools that
          encourage digital making!
        </p>
      </div>

      <div class="card-content">
        <h2 class="section-title">Design Your Lesson</h2>

        <div class="form-container">
          <div class="form-field">
            <label class="field-label">Lesson Topic</label>
            <div class="input-wrapper">
              <SearchIcon width="24" height="24" color="#6c737f" />
              <input
                v-model="formData.topic"
                type="text"
                class="form-input"
                placeholder="e.g., Climate Change, Ancient Civilizations, Fractions..."
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-field">
              <label class="field-label">Grade Level</label>
              <SingleSelect
                v-model="selectedGrade"
                :options="gradeOptions"
                placeholder="Select grade"
                variant="form"
                :disabled="isLoadingGrades"
                @change="handleGradeChange"
              />
            </div>

            <div class="form-field">
              <label class="field-label">Subject</label>
              <SingleSelect
                v-model="selectedSubject"
                :options="subjectOptions"
                placeholder="Select subject"
                variant="form"
                :disabled="isLoadingSubjects"
                @change="handleSubjectChange"
              />
            </div>
          </div>

          <div class="button-container">
            <ActionButton
              @click="handleBack"
              variant="outline"
              class="back-button"
              style="margin-bottom: 0px"
            >
              Back</ActionButton
            >
            <ActionButton
              @click="handleGenerate"
              variant="primary"
              class="generate-button"
              >Generate Lesson
            </ActionButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import ActionButton from "@/components/common/ActionButton.vue";
import CreateIcon from "@/components/icons/CreateIcon.vue";
import SearchIcon from "@/components/icons/SearchIcon.vue";
import SingleSelect from "@/components/common/SingleSelect.vue";
import { ref, defineEmits, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import termService from "@/services/termService";
import LocalStorageService from "@/services/localStorageService";

const emit = defineEmits(["back", "generate"]);

const router = useRouter();
const store = useStore();

const formData = ref({
  topic: "",
});

const selectedGrade = ref("");
const selectedSubject = ref("");
const gradeOptions = ref([]);
const gradeOptionsMap = ref(new Map());
const subjectOptions = ref([]);
const subjectOptionsMap = ref(new Map());
const isLoadingGrades = ref(false);
const isLoadingSubjects = ref(false);

const loadGradeOptions = async () => {
  try {
    isLoadingGrades.value = true;
    const response = await termService.getFilters("grades");
    if (response?.data?.data && Array.isArray(response.data.data)) {
      gradeOptions.value = response.data.data.map((item) => item.name);
      gradeOptionsMap.value = new Map(
        response.data.data.map((item) => [item.name, item])
      );
      if (gradeOptions.value.length > 0 && !selectedGrade.value) {
        selectedGrade.value = gradeOptions.value[0];
      }
    }
  } catch (error) {
    console.error("Error loading grade options:", error);
    gradeOptions.value = [];
  } finally {
    isLoadingGrades.value = false;
  }
};

const loadSubjectOptions = async () => {
  try {
    isLoadingSubjects.value = true;
    const response = await termService.getAllSubjects();
    if (response?.data?.data && Array.isArray(response.data.data)) {
      subjectOptions.value = response.data.data.map((item) => item.name);
      subjectOptionsMap.value = new Map(
        response.data.data.map((item) => [item.name, item])
      );
      if (subjectOptions.value.length > 0 && !selectedSubject.value) {
        selectedSubject.value = subjectOptions.value[0];
      }
    }
  } catch (error) {
    console.error("Error loading subject options:", error);
    subjectOptions.value = [];
  } finally {
    isLoadingSubjects.value = false;
  }
};

onMounted(async () => {
  await Promise.all([loadGradeOptions(), loadSubjectOptions()]);
});

const handleGradeChange = () => {
  // Grade change is handled by v-model
};

const handleSubjectChange = () => {
  // Subject change is handled by v-model
};

const handleBack = () => {
  emit("back");
};

const handleGenerate = () => {
  const gradeData = gradeOptionsMap.value.get(selectedGrade.value);
  const subjectData = subjectOptionsMap.value.get(selectedSubject.value);

  const formDataPayload = {
    topic: formData.value.topic,
    grade_term_id: gradeData?.slug || null,
    grades: selectedGrade.value || "",
    subject_term_id: subjectData?.slug || null,
    subject: selectedSubject.value || "",
  };

  try {
    LocalStorageService.setFormData(formDataPayload);
    store.commit("SET_FORM_DATA", formDataPayload);
  } catch (error) {
    console.error("Error saving form data:", error);
  }

  emit("generate", formDataPayload);

  router.push({
    path: "/",
    query: {
      autogen: "1",
      key: "step-2",
    },
  });
};
</script>

<style scoped>
.create-lesson {
  background-color: #f2f3fb;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  min-height: 100vh;
  width: 100%;
}

.lesson-card {
  background-color: white;
  border: 1px solid #c0c0fd;
  border-radius: 16px;
  width: 100%;
  max-width: 900px;
}

.card-header {
  background-color: var(--purple-dark-1);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 16px 16px 0 0;
  align-items: center;
  padding: 32px;
  width: 100%;
}

.header-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: 45px;
  line-height: 52px;
  color: white;
  text-align: center;
  margin: 0;
}

.card-subtitle {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: white;
  text-align: center;
  margin: 0;
  letter-spacing: 0.08px;
}

.card-content {
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
}

.section-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-large);
  line-height: 28px;
  color: #4c4ddc;
  text-align: center;
  margin: 0;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 21px;
  width: 100%;
  max-width: 800px;
  padding: 26px;
  background-color: white;
  border-radius: 12px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.field-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #3b4648;
  letter-spacing: 0.024px;
}

.input-wrapper {
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 4px 16px;
  height: 40px;
  border: 1px solid #6c737f;
  border-radius: 8px;
  background-color: white;
}

.form-input {
  flex: 1;
  border: none;
  outline: none;
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #3b4648;
  letter-spacing: 0.024px;
  background-color: transparent;
}

.form-input::placeholder {
  color: #98a2b3;
}

.form-row {
  display: flex;
  gap: 8px;
  width: 100%;
}

.form-row .form-field {
  flex: 1;
}

.button-container {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.back-button {
  background-color: white;
  border: 1px solid #bac1cc;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  height: 56px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 12px;
  min-width: 126px;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #6c737f;
  white-space: nowrap;
  letter-spacing: 0.024px;
  transition: all 0.3s ease;
}

.back-button:hover {
  background-color: #f2f3fb;
  border-color: #0478ce;
  color: #3b4648;
}

.generate-button {
  background-color: #4c4ddc;
  border: none;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  height: 56px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 12px;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: white;
  white-space: nowrap;
  letter-spacing: 0.024px;
  transition: all 0.3s ease;
}

.generate-button:hover {
  background-color: #3d3dcf;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(76, 77, 220, 0.3);
}

@media (max-width: 768px) {
  .section-title {
    margin-top: 32px;
  }

  .form-row {
    flex-direction: column;
  }

  .card-content {
    padding: 0px;
    gap: 0px;
  }

  .card-title {
    font-size: 32px;
    line-height: 40px;
  }

  .back-button,
  .generate-button {
    width: 100%;
  }

  .generate-button,
  .back-button {
    font-size: var(--fs-small);
  }
}
</style>
